/**
 * Fiche « Côté Kayla » — SA CANDIDATURE, puis SON TEST. Rien d'autre.
 *
 * ⛔ LE TEST DE LA RÉGIE EST MORT, ET LA FICHE LE RACONTAIT ENCORE.
 * Kayla, 19-08-2026 : « il y a encore la partie décision de la Régie, alors
 * que c'est du legacy […] je veux les informations en rapport avec leur
 * candidature et le test qu'ils ont fait, le reste on s'en fout. » Ce n'était
 * pas qu'un encombrement : depuis que l'épreuve de VENTE a remplacé les cartes
 * de décision (18-08), `score_snapshot` n'a plus de clé `score` — par
 * conception, « la conversation se lit, elle ne se note pas ». Or
 * `score_candidate` lit `score` avec un défaut à 0. Donc TOUT candidat de la
 * nouvelle épreuve arrivait ici avec `best_score = 0`, et la fiche affichait,
 * pour quelqu'un qui a fait 7/10 en vrai :
 *   « meilleur 0/100 » · « Score 0 / 100 » · une barre rouge à 0 ·
 *   « Seuil non atteint (0/80) » · « ⛔ Pas encore — Manque : le score. »
 * Mesuré en prod le 19-08 : 2 passations sur 2 en épreuve de vente, aucune
 * avec un `score`. Le verdict de la fiche était donc FAUX pour 100 % des
 * candidats vivants — pas seulement bavard.
 *
 * Ont donc disparu, avec le test qui les produisait : la courbe de
 * progression et son seuil, le pilier « Décision » (fautes attrapées, « elle
 * invente », faux positifs, time-wasters, vente 2 étages), le barème gradué,
 * le tableau des cartes, le bandeau 🚩 (alimenté par `dangerous_sent`, une clé
 * que la vente n'écrit jamais), le bloc « Rythme » (sa médiane sort du même
 * endroit et vaut « — » pour tout le monde) et la ligne de sessions/scores de
 * l'en-tête. « Stabilité » part aussi : `dropouts_pct` n'est pas dans
 * `ConnectionDto`, la case affichait « — » depuis toujours.
 *
 * 🪤 Le critère « le score » compte encore dans `verdict.recruitable` CÔTÉ
 * SERVEUR — c'est lui qui écrit « Manque : le score ». Il part dans la PR
 * backend qui suit celle-ci, jamais l'inverse : le cockpit se déploie AVANT
 * Cyrano, donc c'est l'écran qui cesse de lire d'abord, et le contrat qui
 * cesse d'envoyer ensuite.
 *
 * ⛔ MODALE, pas tiroir. Kayla, 18-08 : « t'as fait un volet à droite, ça
 * j'aime pas, je voudrais plutôt que ça soit une modal ». La fiche est ce
 * qu'on regarde en entier avant de trancher sur quelqu'un : un volet la range
 * sur le côté, une modale la met devant.
 *
 * Écarts documentés vs la maquette d'origine :
 *   - Meta d'en-tête : `CandidateDetail` n'a ni ville ni source — on affiche
 *     sexe/âge + e-mail + Discord + fuseau IANA + « quand il peut commencer »
 *     + situation.
 *   - Disponibilités : grille 7 jours × N créneaux (donnée réelle du
 *     contrat), lue avec la grille que le candidat A VUE.
 *   - Actions Écarter/Vivier/Embaucher : « Embaucher » part sur le réseau
 *     (invitation cockpit) ; « Écarter » aussi depuis le 12-09-2026 — Kayla :
 *     « que le bouton Écarté fonctionne vraiment, que ça les archive ». Il
 *     pose `archived_at` côté Cyrano (`POST …/archive`), le candidat sort de
 *     la table (bande « N écartés »), et la fiche propose « Réintégrer ».
 *     « Mettre au vivier » reste local : aucun endpoint ne le porte.
 */
import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAction, useQuery as useConvexQuery } from "convex/react";
import { ConvexError } from "convex/values";

import { api as convexApi } from "@/../convex/_generated/api";
import { useActiveAccountId } from "@/core/auth/activeAccount";
import {
  Alerte,
  Badge,
  Button,
  Carte,
  EnTeteDePanneau,
  Etiquette,
  Grille,
  Infobulle,
  Ligne,
  Lignes,
  Modal,
  ModalBody,
  ModalHeader,
  Section,
  Temps,
  Tuile,
  Vide,
  type TonBadge,
} from "@/shared/ui";
import { Ban, Loader2, MailPlus, Undo2 } from "@/shared/ui/icons";

import { adminApi, IS_MOCK_MODE } from "../api";
import {
  CRITERION_ICONS,
  DAY_LABELS,
  SHIFTS,
  START_AVAILABILITY_LABELS,
  WORK_SITUATION_LABELS,
  memeGrille,
} from "../constants";
import { FUSEAU_DES_SHIFTS, minutesToHHMM } from "@/shared/shiftTime";
import { formatDurationMinSec, formatDwellShort, statusLabel } from "../format";
import {
  ETAPES,
  LIBELLES_ETAPES,
  type Etape,
} from "../candidate/vente/scenario";
import { DAY_KEYS } from "../types";
import type {
  CandidateDetail,
  CriterionStatus,
  VenteGestePayload,
  DayKey,
} from "../types";

interface CandidateFicheProps {
  candidateId: string | null;
  onClose: () => void;
}

/**
 * Le ton de sens d'un critère (`ok` validé · `warn` attention · `bad` erreur) et
 * la marque qui l'accompagne. Les couleurs viennent du `Badge` et des jetons
 * `text-valide|attention|erreur`, jamais d'un `emerald-` / `red-` écrit à la main.
 */
const TON: Record<CriterionStatus, { badge: TonBadge; texte: string; marque: string }> = {
  ok: { badge: "vert", texte: "text-valide", marque: "✓" },
  warn: { badge: "ambre", texte: "text-attention", marque: "!" },
  bad: { badge: "rouge", texte: "text-erreur", marque: "✕" },
};

/** Un Badge qui peut porter une phrase : il passe à la ligne au lieu de déborder du téléphone. */
const BADGE_PHRASE = "h-auto min-h-5 max-w-full whitespace-normal py-1 text-left leading-snug";

export function CandidateFiche({ candidateId, onClose }: CandidateFicheProps) {
  const detailQuery = useQuery({
    queryKey: ["regie-test-admin-candidate-detail", candidateId],
    queryFn: () => adminApi.getCandidateDetail(candidateId as string),
    enabled: candidateId !== null,
    // La fiche d'un candidat qui a fini son test ne bouge plus. Rouvrir celle
    // qu'on vient de fermer — le geste de quelqu'un qui compare deux profils —
    // doit être instantané, pas un aller-retour de plus.
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
  const detail = detailQuery.data;

  return (
    <Modal
      isOpen={candidateId !== null}
      onClose={onClose}
      size="xl"
      ariaLabel="Fiche candidat"
    >
      {/* L'en-tête dit le nom et où en est sa candidature (deux lignes, dont
          une dérivée du détail chargé). */}
      <ModalHeader
        title={detail ? nomAffiche(detail) : "Fiche candidat"}
        subtitle={
          detail
            ? `${statusLabel(detail.status ?? "created")}${detail.created_at ? ` · reçu le ${Temps.texteDeLHeure(Date.parse(detail.created_at), FUSEAU_DES_SHIFTS, "jour")}` : ""}`
            : undefined
        }
        onClose={onClose}
      />
      <ModalBody>
        {detailQuery.isLoading && <Vide taille="sm" titre="Chargement…" />}
        {detailQuery.isError && (
          <Alerte ton="erreur" texte="Impossible de charger la fiche de ce candidat." />
        )}
        {detail && candidateId && <FicheBody candidateId={candidateId} detail={detail} />}
      </ModalBody>
    </Modal>
  );
}

/**
 * Le nom, lu À PLAT sur la réponse.
 *
 * 🪤 Ce helper existe parce que la fiche lisait `detail.profile.first_name` et
 * que le serveur n'a jamais renvoyé de `profile` : en vrai mode, ouvrir une
 * fiche jetait un TypeError et le tiroir restait blanc. Seul `?mock=1`
 * marchait, parce que les fixtures imbriquaient. On lit le plat et on retombe
 * sur l'ancienne forme uniquement pour ne pas casser les fixtures.
 */
function nomAffiche(detail: CandidateDetail): string {
  const prenom = detail.first_name ?? detail.profile?.first_name ?? "";
  const nom = detail.last_name ?? detail.profile?.last_name ?? "";
  return `${prenom} ${nom}`.trim() || "Candidat";
}

const SEXE_LABEL: Record<string, string> = { homme: "Homme", femme: "Femme" };

function FicheBody({ candidateId, detail }: { candidateId: string; detail: CandidateDetail }) {
  const prenom = detail.first_name ?? detail.profile?.first_name ?? "";
  const nom = detail.last_name ?? detail.profile?.last_name ?? "";
  const email = detail.email ?? detail.profile?.email ?? null;
  const timezone = detail.timezone ?? detail.profile?.timezone ?? null;
  const startAvailability = detail.start_availability ?? detail.profile?.start_availability ?? null;
  const workSituation = detail.work_situation ?? detail.profile?.work_situation ?? null;
  const discord = detail.contact_discord ?? detail.profile?.contact_discord ?? null;

  const metaParts = [
    // Sexe et âge d'abord : c'est ce que Kayla trie dans la table, la fiche
    // doit dire la même chose au même endroit.
    detail.gender ? (SEXE_LABEL[detail.gender] ?? detail.gender) : null,
    detail.age === null || detail.age === undefined ? null : `${detail.age} ans`,
    email,
    discord ? `Discord ${discord}` : null,
    timezone,
    startAvailability
      ? `commence ${START_AVAILABILITY_LABELS[startAvailability].toLowerCase()}`
      : null,
    workSituation ? WORK_SITUATION_LABELS[workSituation] : null,
  ].filter(Boolean);

  const nomComplet = `${prenom} ${nom}`.trim();

  return (
    <div className="flex flex-col gap-4">
      {/* En-tête : qui il est, et ce que le système en pense (le ton de la suggestion). */}
      <EnTeteDePanneau
        niveau="h3"
        visuel={<Tuile nom={nomComplet || "?"} taille={44} />}
        titre={nomComplet || "Candidat"}
        sousTitre={metaParts.join(" · ")}
        actions={
          <Badge ton={TON[detail.verdict.suggestion.cls].badge} size="md" className={BADGE_PHRASE}>
            {detail.verdict.suggestion.txt}
          </Badge>
        }
      />

      {/* Sa candidature : ce qu'il a déclaré et ce qu'on a mesuré de lui */}
      <SaCandidature detail={detail} />

      {/* Son test */}
      {detail.vente ? <EpreuveDeVente vente={detail.vente} /> : <PasDeTest />}

      {/* Actions */}
      <ActionsRow
        candidateId={candidateId}
        archivedAt={detail.archived_at ?? null}
        email={email ?? ""}
        candidateName={nomComplet}
      />
    </div>
  );
}

/**
 * Sa candidature — les trois critères de recrutement, puis ce que la vérif
 * technique a mesuré et les cases qu'il a cochées.
 *
 * ⛔ Un seul bloc, pas deux colonnes. Le pilier « Opérationnel » vivait en
 * face du pilier « Décision » ; celui-ci parti avec le test des cartes, une
 * demi-largeur orpheline n'a plus de sens.
 *
 * 🚫 Il n'y a plus de quatrième critère « le score ». Cf. le docblock du
 * fichier : ce que la nouvelle épreuve produit n'est pas une note.
 */
function SaCandidature({ detail }: { detail: CandidateDetail }) {
  const items: { key: "computer" | "connection" | "availability"; criterion: CandidateDetail["criteria"]["computer"] }[] = [
    { key: "computer", criterion: detail.criteria.computer },
    { key: "connection", criterion: detail.criteria.connection },
    { key: "availability", criterion: detail.criteria.availability },
  ];
  // `cls`, pas `class` : c'est le nom du champ côté serveur. Lu sous le
  // mauvais nom, l'icône tombait sur son défaut pour tout le monde.
  const deviceIcon = detail.device.cls === "computer" ? "💻" : "📱";
  const verdictConnexion = detail.connection.verdict;

  return (
    <Section titre="🪪 Sa candidature">
      <div className="flex flex-col gap-4">
        <div>
          <Badge ton={detail.verdict.recruitable ? "vert" : "rouge"} size="md" className={BADGE_PHRASE}>
            {detail.verdict.recruitable
              ? "✅ Recrutable"
              : `⛔ Pas encore${detail.verdict.reason ? ` — ${detail.verdict.reason}` : ""}`}
          </Badge>
        </div>

        <Lignes>
          {items.map(({ key, criterion }) => (
            <Ligne
              key={key}
              avant={<span aria-hidden>{CRITERION_ICONS[key]}</span>}
              etiquette={criterion.label}
              sous={criterion.detail}
              apres={
                <Badge ton={TON[criterion.status].badge} size="md">
                  {TON[criterion.status].marque}
                </Badge>
              }
            />
          ))}
        </Lignes>

        <Grille colonnes={2} ecart={20}>
          <div className="flex flex-col gap-0.5">
            <Lignes>
              <Ligne
                avant={<span aria-hidden>{deviceIcon}</span>}
                etiquette={detail.device.label}
                sous={detail.device.detail}
              />
            </Lignes>
            {/* 🩸 `pill` est une CHAÎNE (« À confirmer »), pas un objet. Le lire
                comme `{cls, txt}` faisait `TON[undefined]` → TypeError, et
                l'ErrorBoundary avalait la fiche entière. Ça n'arrivait que pour un
                candidat qui n'est PAS sur ordinateur : le bug attendait le bon
                candidat, et Kayla est tombée dessus le 18-08. Le serveur ne pose
                cette pastille que dans ce cas-là — le ton `ambre` est donc le sien.
                Elle a sa ligne : à côté du libellé, elle le coupait sur téléphone. */}
            {detail.device.pill && (
              <div className="pb-1 pl-[42px]">
                <Badge ton="ambre" size="md">
                  {detail.device.pill}
                </Badge>
              </div>
            )}
            <Lignes>
              <Ligne
                etiquette="Connexion"
                valeur={
                  verdictConnexion === null ? (
                    <span className="text-faint">Non testée</span>
                  ) : (
                    <Badge ton={TON[connVerdictTone(verdictConnexion)].badge} size="md">
                      {connVerdictLabel(verdictConnexion)}
                    </Badge>
                  )
                }
              />
              {/* 🚫 `${null} ms` rend « null ms » à l'écran. Une latence jamais
                  mesurée se dit « — », comme partout ailleurs dans cette fiche. */}
              <Ligne
                etiquette="Latence médiane"
                valeur={detail.connection.rtt_median_ms === null ? "—" : `${detail.connection.rtt_median_ms} ms`}
              />
            </Lignes>
          </div>
          <div className="flex flex-col gap-2">
            <Etiquette>Disponibilités</Etiquette>
            <AvailabilityMiniGrid
              availability={detail.availability}
              grid={detail.availability_grid}
            />
          </div>
        </Grille>
      </div>
    </Section>
  );
}

/**
 * Ce qu'on affiche quand il n'y a pas d'épreuve de vente à montrer.
 *
 * ⛔ Ni un vide, ni une note. Deux candidats bien différents tombent ici : celui
 * qui n'est pas allé jusqu'au test, et celui qui a passé les CARTES de La Régie
 * avant le 18-08 — une passation qui existe bel et bien, mais dont la fiche ne
 * sait plus rien dire depuis que ce test a été retiré. Une case vide se lit
 * « il n'a rien fait », ce qui est faux dans le second cas.
 */
function PasDeTest() {
  return (
    <Section titre="💸 Son test">
      <p className="text-[14px] leading-relaxed text-muted">
        Aucune épreuve de vente sur cette candidature — soit il ne l'a pas passée, soit elle est
        antérieure au 18-08 et il a joué les cartes de décision de La Régie, un test qui n'existe
        plus. Ce n'est pas « il a raté ».
      </p>
    </Section>
  );
}

function connVerdictTone(v: "good" | "ok" | "bad"): CriterionStatus {
  return v === "good" ? "ok" : v === "ok" ? "warn" : "bad";
}
function connVerdictLabel(v: "good" | "ok" | "bad"): string {
  return v === "good" ? "✅ Bonne" : v === "ok" ? "🟡 Correcte" : "🔴 Instable";
}

/**
 * Les cases cochées par le candidat, lues avec LA GRILLE QU'IL A VUE.
 *
 * 🩸 Une lettre ne porte pas son sens. Le 18-08-2026 la grille a changé deux
 * fois dans la soirée (4 créneaux de 6 h → 5 → 6 de 4 h) et un candidat qui
 * s'était déclaré dispo le SOIR (`c` = 20:30–02:30) se relisait en
 * APRÈS-MIDI (`c` = 16:30–20:30). Afficher ses cases avec la grille du jour,
 * c'est lui faire dire autre chose que ce qu'il a coché.
 *
 * Trois cas, et on les distingue à l'écran plutôt que de les confondre :
 *   - grille connue et identique à aujourd'hui → rien à signaler ;
 *   - grille connue mais différente → on affiche SES heures, et on le dit ;
 *   - grille inconnue (coché avant qu'on l'enregistre) → on ne peut que lire
 *     avec celle du jour, et il faut le dire — c'est une lecture douteuse,
 *     pas une donnée.
 */
function AvailabilityMiniGrid({
  availability,
  grid,
}: {
  availability: CandidateDetail["availability"];
  grid: CandidateDetail["availability_grid"];
}) {
  const vue = grid && grid.length > 0 ? grid : null;
  const shifts = vue ?? SHIFTS;
  const decalee = vue !== null && !memeGrille(vue, SHIFTS);
  const alerte = vue === null
    ? "Grille inconnue : ces cases ont été cochées avant qu'on enregistre la grille affichée. Elles sont lues avec celle d'aujourd'hui et peuvent ne pas dire ce qu'il voulait."
    : decalee
      ? "Grille d'époque : ce candidat a vu d'autres horaires que ceux d'aujourd'hui. Les heures ci-dessous sont les SIENNES."
      : null;
  return (
    <div className="flex flex-col gap-2">
      {alerte && (
        <Alerte ton="attention" taille="sm" texte={alerte} />
      )}
      {/* La matrice jour × créneau : une case allumée = coché. La marque est ici
          sur ce qui est CHOISI, pas sur un décor. */}
      <div
        className="grid gap-1.5 text-[12px]"
        style={{ gridTemplateColumns: `auto repeat(${shifts.length}, 1fr)` }}
      >
        <div />
        {shifts.map((shift) => {
          const heures = `${shift.name} · ${minutesToHHMM(shift.startMin)}–${minutesToHHMM(shift.endMin)}`;
          return (
            <Infobulle key={shift.key} texte={heures}>
              <div aria-label={heures} className="text-center font-bold text-faint">
                {shift.letter}
              </div>
            </Infobulle>
          );
        })}
        {DAY_KEYS.map((day: DayKey) => (
          <Fragment key={day}>
            <div className="flex items-center pr-1.5 font-semibold text-muted">{DAY_LABELS[day]}</div>
            {shifts.map((shift) => {
              const on = availability?.[day]?.includes(shift.key) ?? false;
              return (
                <div
                  key={`${day}-${shift.key}`}
                  className={on ? "h-5 rounded-md bg-mark" : "h-5 rounded-md bg-sunken"}
                />
              );
            })}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

type LocalDecision = "pool" | null;

type HireState =
  | { kind: "idle" }
  | { kind: "confirm" }
  | { kind: "sending" }
  | { kind: "sent" }
  | { kind: "error"; message: string };

/**
 * Actions de décision. « Embaucher » branche la VRAIE invitation cockpit :
 * `invitations.send` (action Convex) → invitation WorkOS + e-mail brandé à
 * l'adresse renseignée au test, rôle Chatter (slug `viewer`) dans l'org active
 * de Kayla. Au 1er login, le candidat tombe sur le gate existant « Finalise
 * ton compte » (Discord OAuth + adresse USDC, LIVE prod #242).
 *
 * « Écarter » est réel lui aussi (12-09-2026) : `archived_at` posé côté
 * Cyrano, la liste et la fiche invalidées pour que la table le range sous sa
 * bande sans rechargement. Réversible par « Réintégrer » sur la même fiche.
 * « Mettre au vivier » reste local (pas d'endpoint de persistance dédié).
 */
function ActionsRow({
  candidateId,
  archivedAt,
  email,
  candidateName,
}: {
  candidateId: string;
  archivedAt: string | null;
  email: string;
  candidateName: string;
}) {
  const [decision, setDecision] = useState<LocalDecision>(null);
  const [hire, setHire] = useState<HireState>({ kind: "idle" });

  const queryClient = useQueryClient();
  const ecarter = useMutation({
    mutationFn: (sens: "archive" | "unarchive") =>
      sens === "archive"
        ? adminApi.archiveCandidate(candidateId)
        : adminApi.unarchiveCandidate(candidateId),
    onSuccess: async () => {
      // La table (liste) ET cette fiche (détail) relisent : le candidat passe
      // sous la bande « écartés », et le bouton devient « Réintégrer ».
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["regie-test-admin-candidates"] }),
        queryClient.invalidateQueries({
          queryKey: ["regie-test-admin-candidate-detail", candidateId],
        }),
      ]);
    },
  });
  const estEcarte = archivedAt !== null;

  const accountId = useActiveAccountId();
  // En mode maquette (?mock=1) il n'y a pas de session Convex : on ne query
  // pas les rôles et « Embaucher » affiche une note de démo.
  const rolesData = useConvexQuery(
    convexApi.roles.listAssignableForAccount,
    !IS_MOCK_MODE && accountId ? { accountId } : "skip",
  );
  // ⚠️ Le slug réel du rôle Chatter est « chatter » (vérifié en prod 16-07 :
  // les rôles de l'org sont ceo/manager/operator/chatter/va — « viewer »
  // n'existe pas, le bouton Embaucher échouait silencieusement avec
  // « Rôle introuvable »). Même slug que l'auto-invitation Convex
  // (CANDIDATE_INVITE_ROLE_SLUG).
  const chatterRole =
    rolesData?.roles.find((r) => r.slug === "chatter") ?? null;
  const sendInvitation = useAction(convexApi.invitations.send);

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email ?? "").trim());

  async function invite() {
    if (!accountId || !chatterRole) return;
    setHire({ kind: "sending" });
    try {
      await sendInvitation({
        accountId,
        email: email.trim(),
        roleId: chatterRole._id,
        creatorScopes: [], // [] = accès à tous les créateurs (défaut employé)
      });
      setHire({ kind: "sent" });
    } catch (err) {
      // Les erreurs métier de invitations.send sont des ConvexError avec un
      // {reason} en clair (ex. ALREADY_INVITED). Les throw bruts sont masqués
      // en « Server Error » par Convex en prod → message générique.
      const reason =
        err instanceof ConvexError
          ? (err.data as { reason?: string } | undefined)?.reason
          : undefined;
      setHire({
        kind: "error",
        message: reason ?? "L'invitation n'a pas pu être envoyée.",
      });
    }
  }

  const DECISION_LABEL: Record<Exclude<LocalDecision, null>, string> = {
    pool: "Candidat mis au vivier (noté localement — pas encore de persistance côté serveur).",
  };

  // Ce qui bloque l'invitation réelle (message explicite le cas échéant).
  const blockedReason: string | null = IS_MOCK_MODE
    ? "Mode démo — en vrai, ça enverrait l'invitation cockpit à son e-mail."
    : !emailOk
      ? "Ce candidat n'a pas d'e-mail valide — impossible de l'inviter."
      : !accountId
        ? "Organisation introuvable (reconnecte-toi au cockpit)."
        : rolesData === undefined
          ? null // rôles en cours de chargement
          : !chatterRole
            ? "Rôle « Chatter » introuvable dans ton organisation."
            : null;

  const busy = hire.kind === "sending" || ecarter.isPending;

  return (
    <div className="flex flex-wrap items-center gap-2.5 px-1 pb-1">
      <span className="mr-auto text-[13px] text-faint">
        {estEcarte ? (
          <>
            Écarté le{" "}
            <b className="font-semibold text-muted">
              {archivedAt ? Temps.formatJour(Date.parse(archivedAt), FUSEAU_DES_SHIFTS, "complet") : "—"}
            </b>
            {" "}— archivé, pas supprimé.
          </>
        ) : (
          "Le système propose. Tu tranches."
        )}
      </span>
      {estEcarte ? (
        <Button
          variant="secondary"
          size="lg"
          icon={
            ecarter.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Undo2 className="h-4 w-4" />
            )
          }
          disabled={busy}
          onClick={() => ecarter.mutate("unarchive")}
        >
          Réintégrer
        </Button>
      ) : (
        <Button
          variant="destructive"
          size="lg"
          icon={
            ecarter.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Ban className="h-4 w-4" />
            )
          }
          disabled={busy}
          onClick={() => {
            setDecision(null);
            setHire({ kind: "idle" });
            ecarter.mutate("archive");
          }}
        >
          Écarter
        </Button>
      )}
      <Button
        variant="secondary"
        size="lg"
        disabled={busy}
        onClick={() => {
          setDecision("pool");
          setHire({ kind: "idle" });
        }}
      >
        Mettre au vivier
      </Button>
      <Button
        variant="accent"
        size="lg"
        icon={<MailPlus className="h-4 w-4" />}
        disabled={busy || hire.kind === "sent"}
        onClick={() => {
          setDecision(null);
          setHire({ kind: "confirm" });
        }}
      >
        Embaucher
      </Button>

      {decision && (
        <div className="w-full text-[13px] text-faint">{DECISION_LABEL[decision]}</div>
      )}

      {ecarter.isError && (
        <Alerte
          ton="erreur"
          taille="sm"
          className="w-full"
          texte={
            estEcarte
              ? "La réintégration n'a pas pu être enregistrée."
              : "L'archivage n'a pas pu être enregistré — le candidat est toujours dans la liste."
          }
        />
      )}

      {/* Flux d'embauche = invitation cockpit */}
      {hire.kind === "confirm" && (
        <Carte remplissage="md" className="flex w-full flex-col gap-2">
          <p className="text-[14px] text-fg">
            Inviter <b>{candidateName || "ce candidat"}</b>
            {emailOk && <span className="text-muted"> ({email})</span>} dans ton
            organisation comme <b>Chatter</b> ?
          </p>
          <p className="text-[13px] text-muted">
            Il reçoit un e-mail pour créer son compte cockpit, puis on lui demande
            de connecter son <b>Discord</b> et son <b>adresse USDC</b>.
          </p>
          {blockedReason ? (
            <Alerte ton="attention" taille="sm" texte={blockedReason} />
          ) : (
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Button
                variant="accent"
                size="lg"
                icon={<MailPlus className="h-4 w-4" />}
                disabled={rolesData === undefined}
                onClick={() => void invite()}
              >
                Envoyer l'invitation
              </Button>
              <Button variant="secondary" size="lg" onClick={() => setHire({ kind: "idle" })}>
                Annuler
              </Button>
            </div>
          )}
        </Carte>
      )}

      {hire.kind === "sending" && (
        <div className="flex w-full items-center gap-2 text-[13px] text-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Envoi de l'invitation…
        </div>
      )}

      {hire.kind === "sent" && (
        <Alerte
          ton="ok"
          taille="sm"
          className="w-full"
          texte={<>Invitation envoyée à {email} — il va recevoir l'e-mail pour rejoindre le cockpit.</>}
        />
      )}

      {hire.kind === "error" && (
        <Alerte
          ton="erreur"
          taille="sm"
          className="w-full"
          texte={hire.message}
          action={
            <Button variant="secondary" size="sm" onClick={() => setHire({ kind: "confirm" })}>
              Réessayer
            </Button>
          }
        />
      )}
    </div>
  );
}

/**
 * L'épreuve de vente sur la fiche — la mécanique, puis CE QU'IL A ÉCRIT.
 *
 * L'ordre n'est pas cosmétique. La machine ne sait dire que des choses
 * vérifiables (a-t-il proposé une offre avec son prix, le prix tient-il dans
 * la fourchette) et
 * elles tiennent en dix lignes ; ce qui décide d'une embauche, c'est de savoir
 * s'il donne envie de payer, et ça ne se lit qu'en lisant. Le bloc du bas est
 * donc le plus gros de la fiche, et il n'a volontairement aucune note à côté.
 */
/**
 * Les temps de la scène, et ce qu'il a fait de chacun.
 *
 * Kayla, 18-08-2026 : « pour chaque étape, qu'est-ce qu'il a fait ? ». La
 * donnée arrivait déjà dans la réponse (`vente.gestes`) et n'était rendue nulle
 * part : la fiche ne montrait que le compte mécanique et le bloc de texte. On
 * voyait donc QU'il avait raté, jamais OÙ.
 *
 * ⚠️ On part de la LISTE DES TEMPS, pas des gestes reçus. Une étape traversée
 * sans rien faire ne produit AUCUN geste — la lire depuis la liste des gestes
 * la ferait disparaître de l'écran, et c'est précisément le temps qu'il a perdu
 * qu'on veut voir. `SOURCE_LABEL` distingue ensuite le script recopié du texte
 * écrit : les deux réussissent la mécanique, ils ne disent pas la même chose
 * d'un candidat.
 */
const SOURCE_LABEL: Record<string, string> = {
  script: "script",
  script_modifie: "script retouché",
  francis: "Francis",
  francis_modifie: "Francis retouché",
  ecrit: "écrit",
  // Le chemin d'un flow ou d'une vente (scène du 06-10-2026).
  a_faire: "« À faire maintenant »",
  menu: "menu Flow",
  vente: "bouton $",
};

/**
 * ⏱️ Les marques du CHRONO (06-10-2026) — `debut` / `fin` de chaque étape,
 * rangées parmi les gestes pour ne pas toucher au contrat du serveur. Elles ne
 * sont pas des gestes du candidat : on les retire de la liste et on en tire la
 * durée de l'étape.
 */
const TYPES_DU_CHRONO: ReadonlySet<string> = new Set(["debut", "fin"]);

/** Le temps passé sur une étape, d'après ses marques — null sans les deux. */
function dureeDeLEtape(gestes: VenteGestePayload[], etape: string): number | null {
  const debut = gestes.find((g) => g.etape === etape && g.type === "debut");
  const fin = gestes.find((g) => g.etape === etape && g.type === "fin");
  return debut && fin ? Math.max(0, fin.a_ms - debut.a_ms) : null;
}

/**
 * 🩸 LA SCÈNE A CHANGÉ DE FORME, LES FICHES DÉJÀ PASSÉES NON.
 *
 * Jusqu'au 19-08-2026 l'épreuve comptait HUIT temps ; l'ultimatum est passé à
 * Cyrano et les deux objections ont fusionné, il en reste six. Or ce bloc
 * itérait sur `ETAPES` d'aujourd'hui : les gestes rangés sous `ultimatum`,
 * `objection_prix` et `objection_apercu` auraient disparu de TOUTES les
 * candidatures déjà en base — sans erreur, sans trou visible, juste des lignes
 * en moins sur la fiche d'un candidat qu'on est en train de juger.
 *
 * C'est le même défaut que celui contre lequel le commentaire ci-dessus met en
 * garde, d'un cran plus haut : là on perdait un temps sans geste, ici on
 * perdrait un geste sans temps.
 *
 * La sortie est de figer l'ANCIENNE scène ici, avec ses libellés d'alors — un
 * registre mort, qui ne suit plus `scenario.ts` et ne doit plus bouger. Une
 * fiche est lue des mois plus tard : la seule chose qui doit y rester vraie,
 * c'est ce qui s'est passé le jour de la passation.
 *
 * 🪦 Et il y a maintenant DEUX scènes mortes, pas une. Le 29-08-2026, la
 * carte-menu a été retirée du poste et le temps `menu` est devenu `proposer` —
 * les candidatures passées entre le 19-08 et le 29-08 ont donc joué une scène
 * à six temps qui n'est ni celle d'aujourd'hui, ni celle de huit. D'où une
 * LISTE de scènes, essayées de la plus récente à la plus ancienne : on retient
 * la première qui explique tous les gestes reçus.
 *
 * 🪦 Et TROIS depuis le 06-10-2026 : la scène est devenue le poste entier (la
 * file, le flow « script 1 », le message à 50, la transaction, Reprendre) ;
 * celle du 29-08 rejoint le registre mort, telle quelle.
 *
 * ⛔ Sans cette liste, une fiche du 20-08 serait tombée sur la scène de huit
 * temps — la seule qui restait — et se serait affichée avec trois temps
 * fantômes (`ultimatum`, `objection_prix`, `objection_apercu`) qu'elle n'a
 * jamais eus, marqués « pas traversés ». On aurait reproché à un candidat de
 * n'avoir pas joué des temps qui n'existaient pas le jour de sa passation.
 */
/** Du 29-08 au 06-10-2026 : la carte retirée, `menu` devenu `proposer`. */
const SCENE_DU_29_08: ReadonlyArray<{ etape: string; libelle: string }> = [
  { etape: "tarifs", libelle: "Demande-lui s'il veut les prix" },
  { etape: "proposer", libelle: "Propose-lui UNE offre, avec son prix" },
  { etape: "expliquer", libelle: "Dis ce qu'il y a dans l'offre" },
  { etape: "chauffer", libelle: "Il hésite — donne-lui envie" },
  { etape: "transaction", libelle: "Monte la transaction" },
  { etape: "rendre", libelle: "Rends la main à Cyrano" },
];

const SCENE_DU_19_08: ReadonlyArray<{ etape: string; libelle: string }> = [
  { etape: "tarifs", libelle: "Demande-lui s'il veut les prix" },
  { etape: "menu", libelle: "Envoie la carte avec /menu" },
  { etape: "expliquer", libelle: "Dis ce qu'il y a dans l'offre" },
  { etape: "chauffer", libelle: "Il hésite — donne-lui envie" },
  { etape: "transaction", libelle: "Monte la transaction" },
  { etape: "rendre", libelle: "Rends la main à Cyrano" },
];

const SCENE_DU_18_08: ReadonlyArray<{ etape: string; libelle: string }> = [
  { etape: "ultimatum", libelle: "Dis-lui que la suite est payante" },
  { etape: "tarifs", libelle: "Propose-lui tes tarifs" },
  { etape: "menu", libelle: "Envoie la carte avec /menu" },
  { etape: "expliquer", libelle: "Dis ce qu'il y a dans l'offre" },
  { etape: "objection_prix", libelle: "Il trouve ça cher — tiens bon" },
  { etape: "objection_apercu", libelle: "Il veut voir avant — réponds" },
  { etape: "transaction", libelle: "Monte la transaction" },
  { etape: "rendre", libelle: "Rends la main à Cyrano" },
];

/** Les scènes mortes, de la plus RÉCENTE à la plus ancienne. */
const SCENES_HISTORIQUES: ReadonlyArray<ReadonlyArray<{ etape: string; libelle: string }>> = [
  SCENE_DU_29_08,
  SCENE_DU_19_08,
  SCENE_DU_18_08,
];

/** La scène d'aujourd'hui, dérivée du registre vivant — l'intro en tête
 *  (06-10-2026), qui n'a que son chrono. */
const SCENE_ACTUELLE: ReadonlyArray<{ etape: string; libelle: string }> = [
  { etape: "intro", libelle: "Lit l'introduction et la vidéo" },
  ...ETAPES.filter((e): e is Exclude<Etape, "fini"> => e !== "fini").map((e) => ({
    etape: e,
    libelle: LIBELLES_ETAPES[e],
  })),
];

/**
 * Quelle scène cette candidature a-t-elle jouée ?
 *
 * On tranche sur les gestes reçus, pas sur une date : un geste rangé sous un
 * temps que la scène d'aujourd'hui ne connaît pas ne peut venir que de
 * l'ancienne. Et ce qui resterait inconnu des deux registres s'affiche quand
 * même, en queue, sous sa clé brute — un temps qu'on ne sait pas nommer vaut
 * mieux qu'un geste effacé.
 */
function sceneDe(gestes: VenteGestePayload[]): ReadonlyArray<{ etape: string; libelle: string }> {
  const vues = new Set(gestes.map((g) => g.etape));
  const explique = (scene: ReadonlyArray<{ etape: string }>) => {
    const connues = new Set(scene.map((t) => t.etape));
    return [...vues].every((e) => connues.has(e));
  };
  // De la plus récente à la plus ancienne : la PREMIÈRE qui explique tous les
  // gestes reçus est celle que ce candidat a jouée. Un `find` et pas un
  // ternaire — il y a deux scènes mortes depuis le 29-08, et il y en aura
  // trois.
  //
  // ⚠️ Le repli est la scène la plus ANCIENNE, pas la plus récente : elle est
  // la plus large, donc celle qui laisse le moins de gestes orphelins quand
  // aucune ne colle. Se replier sur la scène du jour ferait tomber en
  // `orphelines` des temps qu'on sait pourtant nommer.
  const scene =
    (explique(SCENE_ACTUELLE) ? SCENE_ACTUELLE : SCENES_HISTORIQUES.find(explique)) ??
    SCENES_HISTORIQUES[SCENES_HISTORIQUES.length - 1]!;
  const nommees = new Set(scene.map((t) => t.etape));
  const orphelines = [...vues]
    .filter((e) => !nommees.has(e))
    .map((etape) => ({ etape, libelle: etape }));
  return [...scene, ...orphelines];
}

function ParEtape({ gestes }: { gestes?: VenteGestePayload[] }) {
  if (!gestes || gestes.length === 0) {
    return (
      <Section titre="🎬 Étape par étape">
        <p className="text-[14px] leading-relaxed text-muted">
          Pas de relevé par étape sur cette candidature — elle est antérieure au 18-08, où le
          détail n'était pas encore rangé. Ce n'est pas « il n'a rien fait ».
        </p>
      </Section>
    );
  }

  const parEtape = new Map<string, VenteGestePayload[]>();
  for (const g of gestes) {
    if (TYPES_DU_CHRONO.has(g.type)) continue;
    parEtape.set(g.etape, [...(parEtape.get(g.etape) ?? []), g]);
  }
  const temps = sceneDe(gestes).filter(
    // L'intro n'a que son chrono : sur une fiche d'avant le 06-10, elle n'a
    // rien à montrer et ne doit pas s'afficher « pas traversée ».
    (t) =>
      (t.etape !== "intro" || dureeDeLEtape(gestes, "intro") !== null) &&
      // La négociation est arrivée le 06-10-2026 au soir : une fiche passée le
      // matin même va de `proposer` à `transaction` sans elle. On ne lui
      // reproche pas un temps qui n'existait pas encore.
      (t.etape !== "negocier" ||
        gestes.some((g) => g.etape === "negocier") ||
        !gestes.some((g) => g.etape === "transaction")),
  );

  return (
    <Section
      titre="🎬 Étape par étape"
      sousTitre={`les ${temps.length} temps de la scène, dans l'ordre`}
    >
      <ol className="flex flex-col gap-3">
        {temps.map(({ etape, libelle }, i) => {
          const faits = parEtape.get(etape) ?? [];
          const chrono = dureeDeLEtape(gestes, etape);
          // Une étape CHRONOMÉTRÉE a été traversée même sans geste (le flow
          // qu'on laisse tourner) ; son verdict est porté par sa marque de fin.
          const fin = gestes.find((g) => g.etape === etape && g.type === "fin");
          const reussi = fin ? fin.reussi : faits.some((g) => g.reussi);
          const traverse = faits.length > 0 || chrono !== null;
          return (
            <li key={etape} className="flex items-start gap-3">
              <span className="w-5 shrink-0 pt-0.5 text-center text-[15px] font-bold" aria-hidden>
                {reussi ? (
                  <span className="text-valide">✓</span>
                ) : traverse ? (
                  <span className="text-erreur">✕</span>
                ) : (
                  <span className="text-faint">·</span>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="tabular-nums text-[12.5px] text-faint">{i + 1}.</span>
                  <span
                    className={traverse ? "text-[14px] font-semibold text-fg" : "text-[14px] font-semibold text-faint"}
                  >
                    {libelle}
                  </span>
                  {!traverse && (
                    <span className="text-[12.5px] text-faint">— il n'a rien fait ici</span>
                  )}
                  {chrono !== null && (
                    <span className="tabular-nums text-[12.5px] text-faint">
                      ⏱ {formatDurationMinSec(chrono)}
                    </span>
                  )}
                </div>
                {faits.map((g, j) => (
                  <div key={j} className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                    {g.texte ? (
                      <span className="text-[14px] leading-snug text-muted">« {g.texte} »</span>
                    ) : (
                      <span className="text-[14px] italic text-muted">{g.type}</span>
                    )}
                    {g.source && <Badge ton="gris">{SOURCE_LABEL[g.source] ?? g.source}</Badge>}
                    <span className="tabular-nums text-[12px] text-faint">
                      {formatDwellShort(g.a_ms)}
                    </span>
                  </div>
                ))}
              </div>
            </li>
          );
        })}
      </ol>
    </Section>
  );
}

function EpreuveDeVente({ vente }: { vente: NonNullable<CandidateDetail["vente"]> }) {
  return (
    <>
      <Section
        titre="💸 Son test — l'épreuve de vente"
        sousTitre={
          <>
            {vente.reussis}/{vente.total} sur la mécanique
            {/* 🚫 « 477 s » demande un calcul mental pour être lu. La durée est
                déjà en secondes dans le contrat : on la met en minutes ici,
                comme partout ailleurs dans le cockpit. */}
            {vente.duree_secondes === null
              ? ""
              : ` · ${formatDurationMinSec(vente.duree_secondes * 1000)}`}
          </>
        }
      >
        <ul className="flex flex-col gap-2">
          {vente.criteres.map((c) => (
            <li key={c.cle} className="flex items-start gap-3 text-[14px]">
              <span className="w-5 shrink-0 text-center font-bold" aria-hidden>
                {c.reussi === true ? (
                  <span className="text-valide">✓</span>
                ) : c.reussi === false ? (
                  <span className="text-erreur">✕</span>
                ) : (
                  <span className="text-faint">·</span>
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className={c.reussi === null ? "text-faint" : "text-fg"}>{c.libelle}</span>
                {c.note ? <span className="text-muted"> — {c.note}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      </Section>

      <ParEtape gestes={vente.gestes} />

      <Section
        titre="🔥 Ce qu'il a écrit"
        sousTitre="aucune machine ne le note, c'est toi qui lis"
      >
        {vente.ses_messages.length === 0 ? (
          <p className="text-[14px] text-faint">Il n'a rien écrit.</p>
        ) : (
          <ol className="flex flex-col gap-2">
            {vente.ses_messages.map((m, i) => (
              <li key={i}>
                <Carte remplissage="sm" className="text-[14.5px] leading-snug text-fg">
                  <span className="mr-1.5 tabular-nums text-faint">{i + 1}.</span>
                  {m}
                </Carte>
              </li>
            ))}
          </ol>
        )}
      </Section>
    </>
  );
}
